import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useImperativeHandle } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { DateFormat, formatDate } from 'src/helpers/dates';
import { scrollOnError } from 'src/helpers/utils';
import * as Yup from 'yup';
import clsx from 'clsx';
import useModal from '../../../components/modal/useModal';
import { sanitizeFormData } from '../../../components/utils/deepCleanFormData';
import { selectActiveBusiness, selectActiveStore } from '../../../reducers/business/businessSlice';
import { setShowModal } from '../../../reducers/modalSlice';
import { useCreateReservationMutation, useEditReservationMutation, useGetReservationQuery } from '../../../reducers/reservation/reservationApiSlice';
import { useGetStoreDetailsQuery } from '../../../reducers/stores/storeApiSlice';
import { sourceExtraOptions, statusExtraOptions } from '../reservationPages/ResOptions';
import TabDetails from '../reservationPages/TabDetails';
import TabPayments from '../reservationPages/TabPayments';
import useToast from 'src/hooks/useToast';

const ReservationModalBody = () => {
	const intl = useIntl();
	const dispatch = useDispatch();
	const { id, ref, editMode, params } = useModal();
	const [editReservation] = useEditReservationMutation();
	const [createReservation] = useCreateReservationMutation();
	const { _id: activeStoreId } = useSelector(selectActiveStore);
	const { _id: business_id } = useSelector(selectActiveBusiness);
	const { data: reservation, isLoading: isLoadingReservation } = useGetReservationQuery({ path: { id: id } }, { skip: !editMode });
	const { data: store, isLoading: isLoadingStore } = useGetStoreDetailsQuery({
		bid: business_id,
		path: { id: reservation?.item?.store?._id ?? activeStoreId },
	});
	const { toastError } = useToast();

	const validationSchema = Yup.object().shape({
		host: Yup.object().shape({
			fname: Yup.string().required(intl.formatMessage({ id: 'ERROR_MESSAGE.FIRST_NAME' })),
			lname: Yup.string().required(intl.formatMessage({ id: 'ERROR_MESSAGE.LAST_NAME' })),
			phone: Yup.string()
				.test('host.phone-format', intl.formatMessage({ id: 'ERROR_MESSAGE.PHONE_FORMAT' }), (value) => {
					if (!value) return true;
					return /^\+[1-9]\d{5,14}$/.test(value);
				})
				.test('host.phone-required', intl.formatMessage({ id: 'ERROR_MESSAGE.PHONE' }), function (value) {
					const { source } = this?.from?.[1]?.value ? this.from[1].value : {}; // walk up to get the top-level field

					if (source && source === 'phone') {
						return !!value;
					}
					return true;
				}),
			email: Yup.string()
				.email(intl.formatMessage({ id: 'ERROR_MESSAGE.EMAIL_FORMAT' }))
				.test('host.email-required', intl.formatMessage({ id: 'ERROR_MESSAGE.EMAIL' }), function (value) {
					const { source } = this?.from?.[1]?.value ? this.from[1].value : {}; // walk up to get the top-level field

					if (source && source === 'email') {
						return !!value;
					}
					return true;
				}),
		}),
		source: Yup.string().required(),
		time: Yup.string().required(intl.formatMessage({ id: 'ERROR_MESSAGE.AVAILABLE_TIME_SLOT' })),
		area: Yup.object()
			.required(intl.formatMessage({ id: 'ERROR_MESSAGE.AREA_REQUIRED' })) // Ensure the object itself is required
			.test('area', intl.formatMessage({ id: 'ERROR_MESSAGE.AREA_REQUIRED' }), (value) => {
				return value && Object.keys(value).length > 0;
			}),
		status: Yup.string().required(),
		table_ids: Yup.array().test('table-ids-are-strings', intl.formatMessage({ id: 'ERROR_MESSAGE.TABLE_REQUIRED' }), function (value) {
			const { status } = this.parent;

			// Only validate when required
			if (status !== 'arrived' && status !== 'completed') {
				return true;
			}

			// Must be a non-empty array of valid strings
			if (!Array.isArray(value) || value.length === 0) return false;

			const allStrings = value.every((id) => typeof id === 'string' && id.trim() !== '');
			return allStrings;
		}),
	});
	const getPreferences = () =>
		reservation && editMode
			? reservation?.item?.preferences.map((item) => ({
					value: item?._id,
					label: item?.title,
			  })) ?? []
			: [];

	const getArea = () => ({ id: reservation?.item?.area?._id, value: reservation?.item?.area?._id, label: reservation?.item?.area?.title });
	const getTime = (time) => formatDate(time, DateFormat.TIME).toString();
	const getDate = (date) => formatDate(date, DateFormat.DAY_MONTH_YEAR_HYPHEN).toString();

	let initialValues = {};
	useEffect(() => {
		if (!isLoadingReservation) {
			initialValues = {
				store_id: reservation?.item?.store?._id ?? activeStoreId ?? store?._id,
				date: editMode ? getDate(reservation?.item?.date) : new Date(),
				date_utc: editMode ? reservation?.item?.date_utc : new Date().getUTCDate(),
				time: editMode ? getTime(reservation?.item?.date) : undefined,
				people: editMode ? reservation?.item?.people ?? 2 : 2,
				area: editMode ? getArea() ?? {} : {},
				area_id: editMode ? reservation?.item?.area?._id ?? '' : '',
				table_ids: editMode ? reservation?.item?.table_ids ?? [] : [],
				team_notes: editMode ? reservation?.item?.team_notes ?? '' : '',
				message_to_guest: editMode ? reservation?.item?.message_to_guest ?? '' : '',
				guest_comments: editMode ? reservation?.item?.guest_comments ?? '' : '',
				duration_mins: editMode ? reservation?.item?.duration_mins ?? 120 : 120,
				preferences: editMode ? getPreferences() ?? [] : [],
				source: editMode ? reservation?.item?.source ?? sourceExtraOptions[1].value : sourceExtraOptions[1].value,
				status: editMode ? reservation?.item?.status ?? statusExtraOptions[0].value : statusExtraOptions[0].value,
				host: {
					_id: editMode ? reservation?.item?.host?._id ?? undefined : undefined,
					fname: editMode ? reservation?.item?.host?.fname ?? '' : '',
					lname: editMode ? reservation?.item?.host?.lname ?? '' : '',
					email: editMode ? reservation?.item?.host?.email ?? '' : '',
					phone: editMode ? reservation?.item?.host?.phone ?? '' : '',
				},
				guests: [],
				guest_users: editMode ? reservation?.item?.guest_users ?? [] : [],
				guest_new_contact: [],
				tables: editMode ? reservation?.item?.tables ?? [] : [],
				payments: editMode ? reservation?.item?.payments ?? [] : [],
				prepayment: editMode ? reservation?.item?.prepayment ?? {} : [],
				is_quick_edit: editMode ? params?.is_quick_edit ?? false : false,
			};

			if (params?.initialValues) {
				initialValues = {
					...initialValues,
					...params.initialValues,
				};
			}
		}
	}, [isLoadingReservation]);

	const methods = useForm({
		mode: 'onSubmit',
		defaultValues: { ...initialValues },
		resolver: yupResolver(validationSchema),
		shouldFocusError: true,
	});

	const {
		handleSubmit,
		register,
		watch,
		reset,
		resetField,
		control,
		trigger,
		getValues,
		setValue,
		formState: { errors, isSubmitting, dirtyFields },
	} = methods;

	useEffect(() => {
		reset({ ...initialValues });
	}, [reservation, reset]);

	const source = watch('source');
	const status = watch('status');

	useEffect(() => {
		trigger('host.phone'); // re-validate phone when source changes
	}, [source]);

	useEffect(() => {
		if (errors?.table_ids) {
			trigger('table_ids'); // re-validate table_ids
		}
	}, [status]);

	/** Watch change of each entry **/
	// console.log(watch({ ...initialValues }));
	/** Show errors captured by validation **/
	// console.log(errors ? errors : 'Store information submitted successfully');

	useImperativeHandle(ref, () => ({
		onSubmit: () => handleSubmit(onSubmit, scrollOnError)(),
		isSubmitting: isSubmitting,
	}));

	const isEmpty = (data) => {
		return typeof data === 'object'
			? Object.keys(data).length === 0 || Object.values(data).some((element) => element === undefined)
			: Array.isArray(data) && data.length === 0 && typeof data !== 'undefined';
	};

	const closeModal = () => {
		dispatch(setShowModal({ type: 'reservation', show: false }));
		dispatch(setShowModal({ type: 'reservation_new', show: false }));
	};

	const onSubmit = async (data) => {
		const cleanedData = sanitizeFormData(data, {
			preserveEmptyArrays: false,
			preserveEmptyObjects: false,
			preserveDirtyValues: true,
			dirtyValues: dirtyFields,
		});
		const result = { ...cleanedData };
		const preferences = [];
		const guests = [];

		/** Formatting / Assignment **/
		result.date = `${formatDate(data.date, DateFormat.DAY_MONTH_YEAR_HYPHEN)} ${result.time}`;

		if (result.preferences && !isEmpty(result.preferences)) {
			result.preferences.forEach((item) => preferences.push(item.value));
			result.preferences = preferences;
		} else delete result.preferences;

		if (result.host) {
			if (!result?.host?._id) {
				result.host = { ...result.host, type: 'new_contact' };
			} else {
				result.host = { ...result.host, type: 'contact' };
				result.host.id = result.host._id;
				delete result.host._id;
			}
		}

		if (result.guest_users) {
			result.guest_users = result.guest_users.map((guest) => ({ ...guest, id: guest?.id, type: guest?.id ? 'contact' : 'tabol_user' }));
		}

		if (result?.guest_new_contact) {
			result?.guest_new_contact.forEach((guest) => {
				if (guest.type === 'new_contact') {
					guests.push({
						type: guest.type,
						fname: guest.firstname,
						lname: guest.lastname,
						email: guest.email,
						phone: guest.phoneNumber,
					});
				} else {
					guests.push(...result.guest_users, {
						type: guest.type,
						fname: guest.firstname,
						lname: guest.lastname,
						email: guest.email,
						phone: guest.phoneNumber,
						id: guest.id,
					});
				}
			});
		}
		result.guests = guests;

		/** Delete Empty Fields **/
		const emptyCheckKeys = ['guests', 'prepayment', 'payment_ids', 'payments', 'table_ids'];
		emptyCheckKeys.forEach((key) => {
			if (isEmpty(result[key])) {
				delete result[key];
			}
		});

		// Empty conditional checks
		if (result.host?.tabol_user_id === '') {
			delete result.host.tabol_user_id;
		}

		if (result.host?.email === '') {
			delete result.host.email;
		}

		if (result.area_id === '') {
			delete result.area_id;
		}

		// Targeted keys
		const keysToDelete = [
			'guest_id',
			'time',
			'date_utc',
			'hours',
			'stores',
			'area',
			'selectedPhoneNumberOption',
			'guest_type',
			'guest_new_contact',
			'guest_users',
			'guest_comments',
			'is_quick_edit',
		];
		keysToDelete.forEach((key) => {
			if (Object.prototype.hasOwnProperty.call(result, key)) {
				delete result[key];
			}
		});

		/** Submission **/
		if (Object.keys(errors).length === 0) {
			const response = editMode
				? await editReservation({ path: { id: id }, body: { ...result } })
				: await createReservation({ path: { id: id }, body: { ...result } });

			if (response?.data?.success) {
				closeModal();
			} else {
				toastError({
					title: intl.formatMessage({ id: 'GENERAL.OK.ERROR.TXT' }),
					message: response?.error?.data?.message,
				});
			}
		}
	};

	const is_quick_edit = getValues('is_quick_edit') ?? params?.is_quick_edit ?? false;

	return (
		<FormProvider {...methods}>
			<ul
				className={clsx('nav nav-tabs nav-line-tabs d-flex justify-content-center mb-5 fs-3', {
					'd-none': is_quick_edit,
				})}
			>
				<li className="nav-item">
					<a className="nav-link active" data-bs-toggle="tab" href="#kt_tab_details" name="details">
						<FormattedMessage id="RESERVATIONS.MODAL_BODY.DETAILS" />
					</a>
				</li>
				<li className="nav-item">
					<a className="nav-link" data-bs-toggle="tab" href="#kt_tab_payments" name="payments">
						<FormattedMessage id="RESERVATIONS.MODAL_BODY.PAYMENTS" />
					</a>
				</li>
			</ul>
			{!isLoadingReservation && !isLoadingStore ? (
				<div className="tab-content" id="myTabContent">
					<div className="tab-pane fade active show" id="kt_tab_details" role="tabpanel">
						<TabDetails
							register={register}
							control={control}
							watch={watch}
							errors={errors}
							trigger={trigger}
							setValue={setValue}
							reset={reset}
							resetField={resetField}
							store={store}
							isStoreLoading={isLoadingStore}
						/>
					</div>

					<div className="tab-pane fade" id="kt_tab_payments" role="tabpanel">
						<TabPayments
							items={reservation}
							register={register}
							control={control}
							watch={watch}
							errors={errors}
							trigger={trigger}
							setValue={setValue}
							reset={reset}
						/>
					</div>
				</div>
			) : (
				<div className="d-flex justify-content-center">
					<div className="spinner-border" role="status">
						<span className="sr-only">Loading...</span>
					</div>
				</div>
			)}
		</FormProvider>
	);
};

export default ReservationModalBody;
